import type { UserListItemDto } from "Infrastructure/Api/Api";
import { CoinCell } from "Infrastructure/Components/Tables/Cells/CoinCell";
import { NumberCell } from "Infrastructure/Components/Tables/Cells/NumberCell";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useUsersQuery } from "Users/Queries/useUsersQuery";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMemo } from "react";
import { UserAvatar } from "../UserAvatar";
import { XPCell } from "./Cells/XPCell";
import { ReusableTable, type TableColumn } from "./ReusableTable";

interface PlayerStats {
  userID: number;
  firstName: string;
  lastName: string;
  fullName: string;
  xp: number;
  coins: number;
  activeRequests: number;
  activeQuests: number;
  pendingApprovals: number;
  picture?: string;
  position?: string;
}

export const PlayerTable = () => {
  const { t } = useResource();
  const { customFontWeight, customSpacing, colors } = useTheme();
  const {
    data: usersList,
    isLoading: usersLoading,
    error: usersError,
  } = useUsersQuery();

  const isLoading = usersLoading;

  const playerData: PlayerStats[] = useMemo(() => {
    if (!usersList?.items) {
      return [];
    }

    return usersList.items
      .map((user: UserListItemDto) => ({
        userID: user.userID,
        firstName: user.firstName,
        lastName: user.lastName,
        fullName: `${user.firstName} ${user.lastName}`.trim(),
        xp: user.xp,
        coins: user.coins,
        activeRequests: user.activeRequests,
        activeQuests: user.activeQuests,
        pendingApprovals: user.pendingApprovals,
        picture: user.picture ?? undefined,
        position: user.position ?? undefined,
      }))
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [usersList?.items]);

  const columns: TableColumn<PlayerStats>[] = [
    {
      id: "fullName",
      label: t(Resources.Tables.Players.Fields.FullName),
      sortable: true,
      format: (row) => (
        <Stack direction="row" alignItems="center" spacing={customSpacing.lg}>
          <UserAvatar
            picture={row.picture}
            firstName={row.firstName}
            lastName={row.lastName}
            size="sm"
          />
          <Stack direction="column" gap={customSpacing.xs}>
            <Typography
              variant="body2"
              sx={{ fontWeight: customFontWeight.bold }}
            >
              {row.fullName}
            </Typography>
            <Typography variant="body3" sx={{ color: colors.text.secondary }}>
              {row.position}
            </Typography>
          </Stack>
        </Stack>
      ),
    },
    {
      id: "xp",
      label: t(Resources.Tables.Players.Fields.XP),
      align: "right",
      sortable: true,
      format: (row) => <XPCell value={row.xp} />,
    },
    {
      id: "coins",
      label: t(Resources.Tables.Players.Fields.Coins),
      align: "right",
      sortable: true,
      format: (row) => (
        <CoinCell
          value={row.coins}
          suffix={t(Resources.Pages.CreateQuest.Fields.Reward.Suffix)}
        />
      ),
    },
    {
      id: "activeRequests",
      label: t(Resources.Tables.Players.Fields.ActiveRequests),
      align: "right",
      sortable: true,
      format: (row) => <NumberCell value={row.activeRequests} />,
    },
    {
      id: "activeQuests",
      label: t(Resources.Tables.Players.Fields.ActiveQuests),
      align: "right",
      sortable: true,
      format: (row) => <NumberCell value={row.activeQuests} />,
    },
    {
      id: "pendingApprovals",
      label: t(Resources.Tables.Players.Fields.PendingApprovals),
      align: "right",
      sortable: true,
      format: (row) => <NumberCell value={row.pendingApprovals} />,
    },
  ];

  if (usersError) {
    return <Box>{t(Resources.Tables.Players.Error)}</Box>;
  }

  return (
    <ReusableTable
      columns={columns}
      data={playerData}
      getRowKey={(player) => `player-${player.userID}-${player.fullName}`}
      maxHeight="100%"
      enableScroll={true}
      isLoading={isLoading}
    />
  );
};
